import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import { Button, ButtonGroup, CardView, rem } from 'design-system';
import { AppView } from 'design-system/AppView';
import EmptyComponent from 'design-system/EmptyComponent';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import Text from 'design-system/Text';
import Squircle from 'design-system/Squircle';
import IconCheck from 'design-system/icons/IconCheck';
import Modal from 'utils/packages/Modal';
import { isWeb } from '../../../constants';
import { useUploadCsvTransactions } from '../hooks/useUploadCsvTransactions';
import UploadingView from '../components/UploadingView';
const strings = {
    title: 'Import transaction data',
    subtitle: 'Upload a CSV file to add transactions to this account',
    selectFile: 'Select file',
    cardHeader: 'CSV file requirements:',
    cardBullets: ['Should include information of transactions date, reference, amount', 'File size must be under 10MB'],
    uploading: {
        title: 'Uploading...',
        subtitle: 'This may take a moment. Please keep the app open until the upload is complete',
        subtitleWeb: 'This may take a moment. Please stay on the page until the upload is complete',
    },
    errors: {
        pickError: {
            title: 'Upload failed',
            description: `Something went wrong and we couldn't upload your file. Please try again`,
        },
        fileToLarge: {
            title: 'File too large',
            description: 'The selected CSV file must be 10MB or less. Please choose a smaller file and try again',
        },
        importError: {
            title: 'Import failed',
            description: 'The CSV file you selected is not compatible. Ensure your file has the required columns. Please try again',
        },
    },
};
const SQUIRCLE_SIZE = 24;
const ICON_SIZE = 14;
const DummyAccountImportTransactionsScreen = ({ navigation, route, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { uploadAndGetConfig, loading } = useUploadCsvTransactions();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            ...(isWeb && {
                title: strings.title,
                headerTitle: EmptyComponent,
            }),
        });
    }, [colors.text.primary]);
    const handleClick = useCallback(async () => {
        try {
            const configResponse = await uploadAndGetConfig();
            if (configResponse) {
                navigation.navigateDeprecated('DummyAccountImportAddTransactions', {
                    ...route.params,
                    ...configResponse,
                });
            }
        }
        catch (error) {
            if (error instanceof Error) {
                if (error.message === 'Incorrect file format') {
                    Modal.showConfirmation({
                        title: strings.errors.pickError.title,
                        subtitle: strings.errors.pickError.description,
                        confirmButtonTitle: 'OK',
                    });
                }
                else if (error.message === 'File too large') {
                    Modal.showConfirmation({
                        title: strings.errors.fileToLarge.title,
                        subtitle: strings.errors.fileToLarge.description,
                        confirmButtonTitle: 'OK',
                    });
                }
                else {
                    Modal.showConfirmation({
                        title: strings.errors.importError.title,
                        subtitle: strings.errors.importError.description,
                        confirmButtonTitle: 'OK',
                    });
                }
            }
        }
    }, [uploadAndGetConfig, route.params]);
    const content = useMemo(() => (<UploadingView title={strings.uploading.title} subtitle={isWeb ? strings.uploading.subtitleWeb : strings.uploading.subtitle}/>), []);
    const onHideModal = useCallback(() => {
        Modal.hide(undefined);
    }, []);
    const onShowModal = useCallback(() => {
        Modal.show(content, {
            fullscreen: true,
            nonDismissable: true,
            fade: true,
        });
    }, [content]);
    useEffect(() => {
        if (!isWeb) {
            if (loading) {
                onShowModal();
            }
            else {
                onHideModal();
            }
        }
    }, [content, loading, onShowModal, onHideModal]);
    return (<AppView type="secondary" paddingBottom isInTab={isWeb} avoidHeader={!(isWeb && loading)}>
      {isWeb && loading ? (<View style={styles.container}>{content}</View>) : (<>
          <View style={styles.container}>
            <StatusBar barStyle="dark-content" animated/>
            <ScreenInfoHeader title={strings.title} subTitle={strings.subtitle}/>
            <CardView noMarginTop withPadding>
              <View style={styles.cardRowContainer}>
                <Text Text-16>{strings.cardHeader}</Text>
                {strings.cardBullets.map((bullet, index) => (<View style={styles.cardRow} key={index}>
                    <Squircle width={SQUIRCLE_SIZE} height={SQUIRCLE_SIZE} color={colors.buttons.brandGroup}>
                      <IconCheck thick width={ICON_SIZE} height={ICON_SIZE} color={colors.text.brand}/>
                    </Squircle>
                    <View style={styles.textContainer}>
                      <Text TextThin-14 Secondary>
                        {bullet}
                      </Text>
                    </View>
                  </View>))}
              </View>
            </CardView>
            <Spacer flex/>
          </View>
          <Spacer flex/>
          <ButtonGroup>
            <Button brand title={strings.selectFile} onPress={handleClick} isFetching={loading}/>
          </ButtonGroup>
        </>)}
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    cardRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(16),
    },
    cardRowContainer: {
        gap: rem(16),
    },
    textContainer: {
        flex: 1,
    },
}));
export default DummyAccountImportTransactionsScreen;
